import { Button, Space, Spin } from 'antd';
import { useTranslate } from '@tolgee/react';
import {
  useFlagrWelfareCenterValue,
  useFlagrZhixiaModulesValue,
} from 'hooks/business/system/useFlagr';
import bannerImg from './assets/banner.png';
import { isString, once } from 'lodash-es';
import { useRequest } from 'ahooks';
import { lazy, useMemo } from 'react';
import CommonAppCard from './components/CommonAppCard';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { openExternalURL } from 'utils/openExternalURL';
import { buryAppCenterCard } from 'services/etc/bury';
import styled from '@emotion/styled';

const ZhixiaApp = lazy(() => import('./Apps/ZhixiaApp'));
const VOCPlusAIApp = lazy(() => import('./Apps/VOCPlusAIApp'));

const fetchAppsConfig = once(() => {
  return fetch('https://clients.zhisuitech.com/fett-public/appCenterList.json')
    .then((res) => res.json())
    .then((res) => res.apps)
    .catch(() => {
      return [];
    });
});

const AppCenter = () => {
  const zhixiaModulesFlag = useFlagrZhixiaModulesValue();
  const sysLang = useSystemLanguageValue();

  const { loading, data: apps } = useRequest(() => fetchAppsConfig());
  const { welfareCenterConfig: config = [] } = useFlagrWelfareCenterValue() || {};
  const existWelfareToZhixia = config.some(({ redirectPath }) => redirectPath === '/zhixia');

  const { t } = useTranslate();

  const remoteConfigApps = useMemo(() => {
    return apps
      ?.filter((appConfig: any) =>
        config.some(({ redirectPath }) => redirectPath.includes(appConfig.key)),
      )
      ?.map((appConfig: any) => {
        const info =
          appConfig?.infos?.[sysLang] || appConfig?.infos?.['en-US'] || appConfig?.infos?.['zh-CN'];

        let officialLink = '';
        if (appConfig?.officialLinkMap) {
          officialLink =
            appConfig?.officialLinkMap?.[sysLang] ||
            appConfig?.officialLinkMap?.['en-US'] ||
            appConfig?.officialLinkMap?.['zh-CN'];
        } else if (isString(appConfig?.officialLink)) {
          officialLink = appConfig?.officialLink;
        }

        return (
          <CommonAppCard
            key={appConfig.key}
            name={info?.name}
            desc={info?.description}
            icon={<img alt={appConfig.key} src={appConfig.logo} />}
            footer={
              <Space>
                {officialLink && (
                  <Button
                    type="primary"
                    onClick={() => {
                      buryAppCenterCard(appConfig.key, 'detail');
                      openExternalURL(officialLink, '_blank');
                    }}
                  >
                    {t('ViewAppCenter.查看介绍')}
                  </Button>
                )}
                {appConfig?.registerLink && (
                  <Button
                    type="primary"
                    onClick={() => {
                      buryAppCenterCard(appConfig.key, 'reg');
                      openExternalURL(appConfig.registerLink, '_blank');
                    }}
                  >
                    {t('ViewAppCenter.立即注册')}
                  </Button>
                )}
              </Space>
            }
          />
        );
      });
  }, [apps, sysLang, config, t]);

  const showZhixia = useMemo(() => {
    return zhixiaModulesFlag?.appCenterCard || existWelfareToZhixia;
  }, [zhixiaModulesFlag, existWelfareToZhixia]);

  return (
    <div className="p-[16px] w-full min-h-full">
      {loading ? (
        <Spin spinning />
      ) : (
        <>
          <div className="relative h-32 mb-4 bg-[#d6ece2]">
            <img className="w-full h-full object-cover" src={bannerImg} alt="Banner" />
            <div className="absolute top-8 left-36">
              <div className="text-lg font-semibold color-[#009a55]">
                {t('ViewAppCenter.appCenterBannerTitle')}
              </div>
              <div className="text-sm text-content-300">
                {t('ViewAppCenter.appCenterBannerDesc')}
              </div>
            </div>
          </div>
          <Grid>
            <VOCPlusAIApp />
            {showZhixia && <ZhixiaApp />}
            {remoteConfigApps}
          </Grid>
        </>
      )}
    </div>
  );
};

const Grid = styled.div`
  display: grid;
  gap: 16px;
  align-items: stretch;
  grid-template-columns: repeat(2, 1fr);
  @media (min-width: 1560px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (min-width: 2100px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

export default AppCenter;
